/**
 * Production Logger Utility
 * Environment-aware logging for clean production builds
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// Log levels for production filtering
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

// Production log level (only errors and warnings in production)
const PRODUCTION_LOG_LEVEL = LOG_LEVELS.WARN;
const DEVELOPMENT_LOG_LEVEL = LOG_LEVELS.DEBUG;

const currentLogLevel = isProduction ? PRODUCTION_LOG_LEVEL : DEVELOPMENT_LOG_LEVEL;

/**
 * Logger utility with environment-aware filtering
 */
class Logger {
  static error(message, ...args) {
    if (currentLogLevel >= LOG_LEVELS.ERROR) {
      console.error(`🚨 ERROR: ${message}`, ...args);
    }
  }

  static warn(message, ...args) {
    if (currentLogLevel >= LOG_LEVELS.WARN) {
      console.warn(`⚠️ WARN: ${message}`, ...args);
    }
  }

  static info(message, ...args) {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      console.log(`ℹ️ INFO: ${message}`, ...args);
    }
  }

  static debug(message, ...args) {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.log(`🔍 DEBUG: ${message}`, ...args);
    }
  }

  // Special methods for specific use cases
  static payment(message, ...args) {
    // Payment logs are critical, always show in development, errors only in production
    if (isDevelopment) {
      console.log(`💰 PAYMENT: ${message}`, ...args);
    } else {
      // Only log payment errors in production
      if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')) {
        this.error(`Payment: ${message}`, ...args);
      }
    }
  }

  static analytics(message, ...args) {
    // Analytics logs only in development
    if (isDevelopment) {
      console.log(`📊 ANALYTICS: ${message}`, ...args);
    }
  }

  static firebase(message, ...args) {
    // Firebase connection issues are critical
    if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')) {
      this.error(`Firebase: ${message}`, ...args);
    } else if (isDevelopment) {
      console.log(`🔥 FIREBASE: ${message}`, ...args);
    }
  }

  static auth(message, ...args) {
    // Auth issues are critical
    if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')) {
      this.error(`Auth: ${message}`, ...args);
    } else if (isDevelopment) {
      console.log(`🔐 AUTH: ${message}`, ...args);
    }
  }

  static download(message, ...args) {
    // Download failures are critical
    if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed')) {
      this.error(`Download: ${message}`, ...args);
    } else if (isDevelopment) {
      console.log(`⬇️ DOWNLOAD: ${message}`, ...args);
    }
  }
}

// Development helper to show current log level
if (isDevelopment) {
  Logger.debug(`Logger initialized - Environment: ${process.env.NODE_ENV}, Log Level: ${currentLogLevel}`);
}

export default Logger;